/**
 * ThreadListSheet — mobile bottom-sheet thread list (mobile UX round,
 * 2026-08-14).
 *
 * On a phone the horizontal chip strip shows ~2 thread names and switching
 * means blind horizontal scrolling; this sheet is the vertical answer. It is
 * deliberately a THIRD RENDERER of the same data, not a new data source:
 * rows are the fullscreen sidebar's exact anatomy (ThreadSidebarRow family,
 * dark-rail palette), ordering is the shared `orderThreads`, the archive ×
 * routes through the same `shouldRouteCloseToEmptyDelete` rule, and every
 * callback is the SAME handler object the chip strip receives at the host
 * render site — the three thread surfaces can never disagree.
 *
 * Additive by construction: ThreadSwitcherBar's pinned DOM is untouched; the
 * trigger renders host-side beside the strip (mobile only) and the sheet
 * body-portals over the fullscreen takeover.
 *
 * Portal contract (styles.ts:380s doctrine + portalClickGuard): the scrim
 * and sheet BOTH sit at BODY_PORTAL_Z_INDEX and BOTH carry
 * data-prest0n-portal="true" — without the marker, useGlobalChat's
 * outside-click handler would close the whole chat on any sheet tap (the
 * exact defect the 2026-08-14 review caught on the header overflow menu).
 */
import { isRoutineStopped } from './routineStatus';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ThreadSheetScrim,
  ThreadSheetContainer,
  ThreadSheetHandle,
  ThreadSheetHeader,
  ThreadSheetTitle,
  ThreadSheetList,
  ThreadSidebarRow,
  ThreadSidebarRowMain,
  ThreadSidebarRowTitle,
  ThreadSidebarRowMeta,
  ThreadUnreadBadge,
  ThreadChipPinButton,
  ThreadChipCloseButton,
  ThreadNewButton,
} from './styles';
import type { ChatThread } from './types';
import { ARCHIVE_ALWAYS_AVAILABLE, hasUnseenActivity, orderThreads } from './useChatThreads';
import { effectiveUnreadCount } from './threadArrival';
import {
  ThreadTurnIndicator,
  relativeActivityLabel,
  shouldRouteCloseToEmptyDelete,
} from './ThreadSwitcherBar';
import { RoutineGlyph, routineNextRunLabel, routineThreadDisplayName } from './RoutineGlyph';
import { ThreadTypeGlyph } from './ThreadTypeGlyph';
import { ThreadSectionHeader } from './ThreadSectionHeader';
import { ThreadRowActions, ThreadRowRenameInput } from './ThreadRowActions';
import { useThreadDrag } from './useThreadDrag';
import { ThreadListNotice } from './ThreadListNotice';
import { threadListCopy } from './threadListCopy';
import type { ThreadOrganizeApi } from './threadOrganize';
import { ThreadSearch, type ThreadSearchApi } from './ThreadSearch';
import { isGroupParticipantCount } from '../../services/groupThreads/senderIdentity';
import { buildThreadSections, sidebarSectionHook } from './threadSections';
import { canOrganizeThreads } from './threadOrganize';
import {
  threadDisplayKindFor,
  threadDisplayTitleFor,
  type ThreadListIdentityProps,
} from './threadListIdentity';
import { SidebarSectionCount, SidebarSectionHeader } from './Routines.styles';
import { RoutineRowTitleLine, RoutineNextRunMeta, RoutinePausedChip } from './Routines.styles';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';

export interface ThreadListSheetProps extends ThreadListIdentityProps {
  /** THREAD SEARCH (PR-5): the same host API the rail's box uses. Absent → no box. */
  search?: ThreadSearchApi | null;
  /** Focus the search box on open instead of the first row (the sheet opened for search). */
  focusSearchOnOpen?: boolean;
  /** The organization surface (2026-09-09) — the same API the rail wires. */
  organize?: ThreadOrganizeApi | null;
  open: boolean;
  onClose: () => void;
  threads: ChatThread[];
  activeThreadId: string | null;
  processingByThread?: Record<string, boolean>;
  turnStartedAtByThread?: Record<string, number>;
  activeThreadMessageCount?: number;
  participantsByThread?: Record<string, number>;
  onSwitch: (threadId: string) => void;
  onNew: () => void;
  onRequestDelete: (threadId: string) => void;
  onDeleteEmptyThread?: (threadId: string) => void;
  onTogglePin?: (threadId: string) => void;
  /**
   * Docked thread-list round (2026-08-24): the same list wears two forms.
   * 'sheet' (default) is the mobile bottom sheet, unchanged. 'panel' is the
   * docked pane's form — an anchored drop-UP menu above the strip's ☰
   * trigger (SidebarAccountMenu family: dark surface, gold hairline, opens
   * upward), with an invisible click-away layer instead of a page dim.
   * One renderer, one data source, two silhouettes.
   */
  variant?: 'sheet' | 'panel';
  /**
   * Panel-form anchor, measured from the trigger at open time (the
   * HeaderOverflowMenu idiom): distances from the viewport's right and
   * bottom edges. Ignored by the sheet form.
   */
  anchor?: { right: number; bottom: number } | null;
  /**
   * The viewer's uid (per-thread notification badges, 2026-09-08) — see
   * ThreadSwitcherBarProps.viewerUid; the same predicate, the same dot.
   */
  viewerUid?: string | null;
}

export const ThreadListSheet: React.FC<ThreadListSheetProps> = ({
  open,
  onClose,
  threads,
  activeThreadId,
  processingByThread,
  turnStartedAtByThread,
  activeThreadMessageCount,
  participantsByThread,
  onSwitch,
  onNew,
  onRequestDelete,
  onDeleteEmptyThread,
  onTogglePin,
  variant = 'sheet',
  anchor,
  viewerUid,
  participantIdsByThread,
  memberLabels,
  categories,
  search,
  focusSearchOnOpen = false,
  organize,
}) => {
  const sheetRef = useRef<HTMLDivElement>(null);

  // Reference-counted shared lock — nests cleanly with the fullscreen
  // takeover's own lock (threadScrollingUx contract).
  useBodyScrollLock(open);

  // Panel form only: the anchor was measured from the trigger at open time,
  // so a viewport resize makes it stale — close rather than float loose
  // (standard anchored-menu behavior; the scroll case can't drift because
  // the body scroll lock above holds the page still while open).
  useEffect(() => {
    if (!open || variant !== 'panel') return undefined;
    const onResize = () => onClose();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [open, variant, onClose]);

  // Escape closes (capture phase, same contract as ThreadHeaderOverflowMenu:
  // stopPropagation so the chat's own Escape handling doesn't also fire).
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onClose();
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [open, onClose]);

  // Focus contract (review 2026-08-14 — the sibling portals' own pattern:
  // ThreadHeaderOverflowMenu focuses its first menuitem on open and returns
  // focus on close): an aria-modal dialog must take focus, or a keyboard
  // user is left tabbing the chrome BEHIND the scrim. First thread row on
  // open (sheet container as fallback); previously-focused element — the
  // trigger — restored on close/unmount.
  useEffect(() => {
    if (!open || typeof document === 'undefined') return undefined;
    const previouslyFocused = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    // Opened FOR SEARCH (the docked magnifier, ⌘K): the box takes focus
    // instead of the first row (PR-C, 2026-09-09).
    const first = (focusSearchOnOpen ? document.querySelector<HTMLElement>('[data-thread-search-input]') : null)
      || document.querySelector<HTMLElement>('[data-sheet-thread-id]')
      || sheetRef.current;
    first?.focus();
    return () => {
      previouslyFocused?.focus();
    };
  }, [open, focusSearchOnOpen]);

  const handleClose = useCallback((e: React.MouseEvent, threadId: string) => {
    e.stopPropagation();
    e.preventDefault();
    const routeToEmptyDelete = shouldRouteCloseToEmptyDelete({
      threadId,
      activeThreadId,
      threads,
      activeThreadMessageCount,
      hasEmptyDeleteHandler: !!onDeleteEmptyThread,
    });
    if (routeToEmptyDelete && onDeleteEmptyThread) {
      // Sheet stays open: the row simply vanishes — no modal is coming.
      onDeleteEmptyThread(threadId);
      return;
    }
    onRequestDelete(threadId);
    // The archive confirm modal takes the screen next — close the sheet so
    // there is exactly ONE layer for Escape/cancel to target. Leaving it
    // open put the modal over a live hidden sheet whose capture-phase
    // Escape listener dismissed the WRONG layer (review 2026-08-14).
    onClose();
  }, [activeThreadId, threads, activeThreadMessageCount, onDeleteEmptyThread, onRequestDelete, onClose]);

  // Organization state (PR-B, 2026-09-09) — hooks stay ABOVE the closed-state
  // early return: a sheet that closes must render the same hook count.
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const drag = useThreadDrag(organize);

  if (!open || typeof document === 'undefined') return null;

  const ordered = orderThreads(threads);
  // PR-5 (2026-09-09): the SAME sections the rail shows — categories, direct
  // messages, group threads, threads (routines first, as before) — from the
  // one shared builder. The sheet stays FLAT while everything is in one
  // section (a lone "threads" header on a phone adds nothing); headers
  // appear the moment a second section exists.
  const listIdentity: ThreadListIdentityProps = {
    participantIdsByThread,
    memberLabels,
    categories,
  };
  const routineRows = ordered.filter((t) => !!t.routineId);
  // Every section, the empty ones included — the sheet renders the non-empty
  // ones; the full list feeds "manage sections…" (2026-09-09).
  const allSections = buildThreadSections({
    rows: ordered.filter((t) => !t.routineId),
    categories: organize?.categories ?? categories,
    kindOf: (t) => threadDisplayKindFor(t, listIdentity, viewerUid, participantsByThread),
    lang: organize?.lang,
    foldAliases: organize?.foldAliases,
    includeEmpty: true,
  });
  const sections = allSections.filter((s) => s.rows.length > 0);
  const visibleSectionIds = sections.map((s) => s.categoryId);
  const sectionCount = sections.length + (routineRows.length > 0 ? 1 : 0);
  // With the organization surface every section header is an affordance
  // (collapse, rename, move) — shown even for a lone section.
  const mayOrganize = canOrganizeThreads(organize);
  const showSectionHeaders = sectionCount > 1 || mayOrganize;
  const organizeCopy = threadListCopy(organize?.lang ?? 'en');

  const renderRow = (thread: ChatThread): React.ReactNode => {
    const isActive = thread.id === activeThreadId;
    const isRoutine = !!thread.routineId;
    // v2 (2026-08-16): group-ness is independent of routine-ness — a routine
    // group thread wears both markers (mirrors the sidebar rule).
    const isGroup = thread.sharedThread === true
      || isGroupParticipantCount(participantsByThread?.[thread.id] ?? 1);
    const rowKind = isRoutine ? null : threadDisplayKindFor(thread, listIdentity, viewerUid, participantsByThread);
    const routinePaused = isRoutineStopped(thread.routineStatus);
    // PR-5: chat rows are called by the one display-title rule (unified
    // rename > the other person in a direct message > the record's title).
    const displayTitle = isRoutine
      ? routineThreadDisplayName(thread)
      : threadDisplayTitleFor(thread, listIdentity, viewerUid);
    const isTurnRunning = !!(processingByThread && processingByThread[thread.id]);
    const turnSinceMs = isTurnRunning ? (turnStartedAtByThread?.[thread.id] ?? null) : null;
    // Phantom-proof count (2026-09-08): zero when the viewer has viewed the
    // thread since its last activity, whatever a stale counter stored.
    const unread = effectiveUnreadCount(thread);
    // PR-3 (2026-09-08): the OPEN thread keeps its badge until the viewer has
    // dwelt on it for 3 s (markThreadViewed) — opening is not reading.
    const showBadge = unread > 0;
    // Per-thread badges (2026-09-08): DOT for server-driven unseen activity.
    const showDot = !showBadge && hasUnseenActivity(thread, activeThreadId, viewerUid);
    // v2: trigger routines have no clock — the cadence line is the trigger
    // summary; multi-routine threads lead with their count.
    const routineCadence = routinePaused
      ? 'Paused'
      : routineNextRunLabel(thread.routineNextRunAt)
        || (thread.routineNextRunAt == null ? (thread.routineScheduleSummary || '') : '');
    const routineMetaText = (thread.routineCount ?? 1) > 1
      ? [`${thread.routineCount} routines`, routineCadence].filter(Boolean).join(' · ')
      : routineCadence;
    return (
      <ThreadSidebarRow
        key={thread.id}
        type="button"
        role="tab"
        aria-selected={isActive}
        aria-label={
          `${isRoutine ? 'routine ' : ''}${displayTitle}${isTurnRunning ? ' (working)' : ''}` +
          (showBadge ? `, ${unread} unread` : showDot ? ', new activity' : '')
        }
        data-sheet-thread-id={thread.id}
        $active={isActive}
        title={thread.topicSummary || displayTitle}
        {...drag.rowProps(thread.id, isRoutine || renamingId === thread.id)}
        onDoubleClick={mayOrganize && organize && !isRoutine ? (e) => { e.preventDefault(); setRenamingId(thread.id); } : undefined}
        onClick={() => {
          if (!isActive) onSwitch(thread.id);
          onClose();
        }}
      >
        <ThreadSidebarRowMain>
          {isRoutine ? (
            <>
              <RoutineRowTitleLine>
                <RoutineGlyph active={!routinePaused} size={12} />
                {isGroup && (
                  <ThreadTypeGlyph kind="group" active={isActive} size={12} data-routine-group-glyph="" />
                )}
                <ThreadSidebarRowTitle $active={isActive}>{displayTitle}</ThreadSidebarRowTitle>
                {routinePaused && (
                  <RoutinePausedChip data-routine-paused-chip="">Paused</RoutinePausedChip>
                )}
              </RoutineRowTitleLine>
              {routineMetaText && (
                <RoutineNextRunMeta data-routine-row-meta="">{routineMetaText}</RoutineNextRunMeta>
              )}
            </>
          ) : (
            <>
              <RoutineRowTitleLine>
                <ThreadTypeGlyph kind={rowKind ?? (isGroup ? 'group' : 'individual')} active={isActive} size={12} />
                {mayOrganize && organize && renamingId === thread.id ? (
                  <ThreadRowRenameInput
                    initial={displayTitle}
                    ariaLabel={organizeCopy.rename}
                    onCommit={(title) => { setRenamingId(null); organize.renameThread(thread.id, title); }}
                    onCancel={() => setRenamingId(null)}
                  />
                ) : (
                  <ThreadSidebarRowTitle $active={isActive}>{displayTitle}</ThreadSidebarRowTitle>
                )}
              </RoutineRowTitleLine>
              <ThreadSidebarRowMeta>
                {relativeActivityLabel(thread.lastActiveAt)}
                {thread.messageCount > 0 ? ` · ${thread.messageCount} messages` : ' · empty'}
              </ThreadSidebarRowMeta>
            </>
          )}
        </ThreadSidebarRowMain>
        {isTurnRunning && <ThreadTurnIndicator sinceMs={turnSinceMs} />}
        {showBadge && (
          <ThreadUnreadBadge aria-hidden="true" data-unread-count={unread} $placement="inset">
            {unread > 99 ? '99+' : String(unread)}
          </ThreadUnreadBadge>
        )}
        {showDot && (
          <ThreadUnreadBadge aria-hidden="true" data-unread="dot" $dot $placement="inset" />
        )}
        {mayOrganize && organize && !isRoutine && (
          <ThreadRowActions
            thread={thread}
            displayTitle={displayTitle}
            organize={organize}
            onStartRename={() => setRenamingId(thread.id)}
            onTogglePin={onTogglePin}
            onArchive={(id) => onRequestDelete(id)}
          />
        )}
        {onTogglePin && (
          <ThreadChipPinButton
            type="button"
            data-thread-pin-btn=""
            data-pinned={thread.pinned === true ? 'true' : 'false'}
            $pinned={thread.pinned === true}
            tabIndex={-1}
            aria-pressed={thread.pinned === true}
            aria-label={thread.pinned ? `unpin thread ${thread.title}` : `pin thread ${thread.title} to the top`}
            title={thread.pinned ? 'unpin thread' : 'pin thread to top'}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onTogglePin(thread.id);
            }}
          >
            <svg viewBox="0 0 12 12" aria-hidden="true">
              <path
                d="M7.2 1.2 L10.8 4.8 L8.6 5.2 L6.4 7.4 L6 10.8 L3.8 8.6 L1.2 11.2 M3.8 8.6 L1.6 6.4 L5 6 L7.2 3.8 Z"
                fill={thread.pinned ? 'currentColor' : 'none'}
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinejoin="round"
              />
            </svg>
          </ThreadChipPinButton>
        )}
        {ARCHIVE_ALWAYS_AVAILABLE && (
          <ThreadChipCloseButton
            type="button"
            data-thread-close-btn=""
            $onDark
            tabIndex={-1}
            aria-label={`archive thread ${thread.title} (Prest0n keeps it in memory for reference; the conversation closes)`}
            title="archive thread"
            onClick={(e) => handleClose(e, thread.id)}
          >
            <svg viewBox="0 0 12 12" aria-hidden="true">
              <path d="M3 3 L9 9 M9 3 L3 9" strokeLinecap="round" />
            </svg>
          </ThreadChipCloseButton>
        )}
      </ThreadSidebarRow>
    );
  };

  return createPortal(
    <>
      <ThreadSheetScrim
        data-thread-sheet-scrim=""
        data-prest0n-portal="true"
        $transparent={variant === 'panel'}
        onClick={onClose}
      />
      <ThreadSheetContainer
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label="threads"
        tabIndex={-1}
        data-thread-sheet=""
        data-thread-sheet-variant={variant}
        data-prest0n-portal="true"
        $variant={variant}
        style={variant === 'panel' && anchor
          ? { right: anchor.right, bottom: anchor.bottom }
          : undefined}
      >
        {variant === 'sheet' && <ThreadSheetHandle aria-hidden="true" />}
        <ThreadSheetHeader>
          <ThreadSheetTitle>
            threads
            {ordered.length > 0 ? ` · ${ordered.length}` : ''}
          </ThreadSheetTitle>
          <ThreadNewButton
            type="button"
            data-thread-sheet-new=""
            aria-label="start a new thread"
            title="new thread"
            onClick={() => {
              onNew();
              onClose();
            }}
          >
            +
          </ThreadNewButton>
        </ThreadSheetHeader>
        {/* THREAD SEARCH (PR-5, 2026-09-09): a hit opens its thread and
            closes the sheet (the same rule as a row pick). */}
        {search && <ThreadSearch api={search} variant="sheet" onOpened={onClose} autoFocus={focusSearchOnOpen} />}
        <ThreadSheetList role="tablist" aria-orientation="vertical">
          {!showSectionHeaders && ordered.map(renderRow)}
          {showSectionHeaders && routineRows.length > 0 && (
            <React.Fragment key="routines">
              <SidebarSectionHeader data-sheet-section="routines">
                <RoutineGlyph size={12} />
                routines
                <SidebarSectionCount>· {routineRows.length}</SidebarSectionCount>
              </SidebarSectionHeader>
              {routineRows.map(renderRow)}
            </React.Fragment>
          )}
          {showSectionHeaders && sections.map((section) => (
            <React.Fragment key={section.key}>
              <ThreadSectionHeader
                section={section}
                hook={sidebarSectionHook(section)}
                hookAttr="data-sheet-section"
                organize={organize}
                index={visibleSectionIds.indexOf(section.categoryId)}
                total={visibleSectionIds.length}
                visibleSectionIds={visibleSectionIds}
                manageSections={allSections}
                dropActive={drag.over === section.categoryId}
                dropHandlers={drag.sectionProps(section.categoryId)}
              />
              {!organize?.collapsed.has(section.categoryId) && section.rows.map(renderRow)}
            </React.Fragment>
          ))}
          {organize && (
            <ThreadListNotice notice={organize.notice} lang={organize.lang} onDismiss={organize.dismissNotice} />
          )}
        </ThreadSheetList>
      </ThreadSheetContainer>
    </>,
    document.body,
  );
};

export default ThreadListSheet;
